import React, { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../../../backend/convex/_generated/api";
import type { Doc } from "../../../../../backend/convex/_generated/dataModel";
import { useOrganization } from "../../hooks/useOrganization";
import { OrganizationAdminLayout } from "../../components/OrganizationAdminLayout";
import { Link } from "react-router-dom";
import { generateSlug } from "../../utils/slug";

export const OrganizationWorkspaces: React.FC = () => {
  const { organization, isAdmin, isLoading } = useOrganization();
  const workspaces = useQuery(api.organizations.listWorkspaces, 
    organization ? { orgId: organization._id } : "skip"
  );
  const createWorkspace = useMutation(api.organizations.createWorkspace);

  const [isCreating, setIsCreating] = useState(false);
  const [newWorkspace, setNewWorkspace] = useState({ name: "", slug: "" });
  const [isSlugEdited, setIsSlugEdited] = useState(false);

  if (isLoading) {
    return (
      <OrganizationAdminLayout title="Workspaces">
        <div>Loading...</div>
      </OrganizationAdminLayout>
    );
  }

  if (!organization || !isAdmin) {
    return (
      <OrganizationAdminLayout title="Workspaces">
        <div className="text-red-500">Unauthorized</div>
      </OrganizationAdminLayout>
    );
  }

  const handleCreateWorkspace = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createWorkspace({
        orgId: organization._id,
        name: newWorkspace.name,
        slug: newWorkspace.slug,
      });
      setIsCreating(false);
      setNewWorkspace({ name: "", slug: "" });
      setIsSlugEdited(false);
    } catch (error) {
      // Error handling is done via Convex error propagation
      // The error will be shown through the UI if needed
      console.error("Failed to create workspace:", error);
      throw error; // Re-throw to let Convex handle it
    }
  };

  const handleNameChange = (name: string) => {
    setNewWorkspace(prev => ({
      ...prev,
      name,
      slug: isSlugEdited ? prev.slug : generateSlug(name)
    }));
  };

  const handleSlugChange = (slug: string) => {
    setIsSlugEdited(true);
    setNewWorkspace(prev => ({ ...prev, slug: slug.toLowerCase().replace(/\s+/g, '-') }));
  };

  return (
    <OrganizationAdminLayout title="Workspaces">
      <div className="flex justify-between items-center mb-8">
        <p className="text-muted-foreground">
          Manage and create workspaces for your organization.
        </p>
        <button
          onClick={() => setIsCreating(true)}
          className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg font-medium transition-colors"
        >
          Create Workspace
        </button>
      </div>

      {isCreating && (
        <div className="mb-8 p-6 bg-card rounded-xl border border-border shadow-sm">
          <h3 className="text-lg font-semibold mb-4">New Workspace</h3>
          <form onSubmit={handleCreateWorkspace} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  required
                  value={newWorkspace.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary outline-none"
                  placeholder="Engineering Team"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Slug</label>
                <input
                  type="text"
                  required
                  value={newWorkspace.slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary outline-none"
                  placeholder="eng-team"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setIsCreating(false); setIsSlugEdited(false); }}
                className="px-4 py-2 text-sm font-medium hover:bg-accent rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium rounded-lg hover:bg-primary/90"
              >
                Create
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {workspaces?.map((workspace: Doc<"workspaces">) => (
          <div key={workspace._id} className="p-6 bg-card rounded-xl border border-border shadow-sm hover:shadow-md transition-shadow">
            <h3 className="font-bold text-lg mb-1">{workspace.name}</h3>
            <p className="text-sm text-muted-foreground mb-4">/w/{workspace.slug}</p>
            <div className="flex gap-2">
              <Link
                to={`/w/${workspace.slug}/admin/assessments`}
                className="text-sm font-medium text-primary hover:underline"
              >
                Manage
              </Link>
            </div>
          </div>
        ))}
        {workspaces?.length === 0 && !isCreating && (
          <div className="col-span-full py-12 text-center bg-muted/20 rounded-xl border border-dashed border-border">
            <p className="text-muted-foreground">No workspaces found. Create your first one!</p>
          </div>
        )}
      </div>
    </OrganizationAdminLayout>
  );
};
